import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
/**
 * Hook para gerenciar testes A/B de autoplay
 *
 * Integra com o autoplayTestService e React Query
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { autoplayTestService, CreateAutoplayTestPayload, UpdateAutoplayTestTitlePayload, UpdateAutoplayTestTrafficPayload } from '@/services/autoplayTestService';
import { AutoplayABTestConfig } from '@/store/videoFeatures/types/autoplay-ab-test';

export const AUTOPLAY_TEST_KEYS = {
  all: ['autoplay-tests'] as const,
  lists: () => [...AUTOPLAY_TEST_KEYS.all, 'list'] as const,
  list: (userId: string) => [...AUTOPLAY_TEST_KEYS.lists(), userId] as const,
  details: () => [...AUTOPLAY_TEST_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...AUTOPLAY_TEST_KEYS.details(), id] as const,
  videos: (id: string) => [...AUTOPLAY_TEST_KEYS.detail(id), 'videos'] as const,
  metrics: (id: string) => [...AUTOPLAY_TEST_KEYS.detail(id), 'metrics'] as const,
};

/**
 * Hook para buscar todos os testes de um usuário
 */
export const useUserAutoplayTests = (userId: string | undefined) => {
  useLanguage();
  return useQuery({
    queryKey: AUTOPLAY_TEST_KEYS.list(userId || ''),
    queryFn: () => autoplayTestService.findUserAutoplayTests(userId!),
    enabled: !!userId,
  });
};

/**
 * Hook para buscar vídeos de um teste
 */
export const useAutoplayTestVideos = (testId: string | undefined) => {
  useLanguage();
  return useQuery({
    queryKey: AUTOPLAY_TEST_KEYS.videos(testId || ''),
    queryFn: () => autoplayTestService.getAutoplayTestVideos(testId!),
    enabled: !!testId,
  });
};

/**
 * Hook para buscar métricas de um teste
 */
export const useAutoplayTestMetrics = (
  testId: string | undefined,
  options?: {
    refetchInterval?: number;
    inicio?: string;
    fim?: string;
  }
) => {
  useLanguage();
  // Calcular datas padrão (últimos 30 dias)
  const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      inicio: startDate.toISOString().split('T')[0],
      fim: endDate.toISOString().split('T')[0]
    };
  };

  const { inicio: defaultInicio, fim: defaultFim } = getDefaultDates();
  const inicio = options?.inicio || defaultInicio;
  const fim = options?.fim || defaultFim;

  return useQuery({
    queryKey: [...AUTOPLAY_TEST_KEYS.metrics(testId || ''), inicio, fim],
    queryFn: () => autoplayTestService.getAutoplayTestMetrics(testId!, inicio, fim),
    enabled: !!testId,
    refetchInterval: options?.refetchInterval || false,
    // Configurações de cache para evitar requisições desnecessárias
    staleTime: 5 * 60 * 1000, // Considerar dados "frescos" por 5 minutos
    gcTime: 10 * 60 * 1000, // Manter cache por 10 minutos (antigo cacheTime)
    refetchOnMount: false, // Não refetch ao montar se já tem dados em cache
    refetchOnWindowFocus: false, // Não refetch ao focar na janela
  });
};

/**
 * Hook para criar teste de autoplay
 */
export const useCreateAutoplayTest = () => {
  useLanguage();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      originalVideoId,
      payload,
    }: {
      originalVideoId: string;
      payload: CreateAutoplayTestPayload;
    }) => autoplayTestService.createAutoplayTest(originalVideoId, payload),
    onSuccess: (data, variables) => {
      toast.success(translateInterfaceText("Teste A/B criado com sucesso!"));
      // Invalidar cache dos testes do usuário
      queryClient.invalidateQueries({
        queryKey: AUTOPLAY_TEST_KEYS.lists()
      });
    },
    onError: (error: any) => {
      toast.error(translateInterfaceText("Erro ao criar teste A/B"), {
        description: error?.response?.data?.message || error.message,
      });
    },
  });
};

/**
 * Hook para atualizar título do teste
 */
export const useUpdateAutoplayTestTitle = () => {
  useLanguage();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      testId,
      payload,
    }: {
      testId: string;
      payload: UpdateAutoplayTestTitlePayload;
    }) => autoplayTestService.updateTestTitle(testId, payload),
    onSuccess: (data, variables) => {
      toast.success(translateInterfaceText("Título atualizado com sucesso!"));
      // Invalidar cache do teste específico
      queryClient.invalidateQueries({
        queryKey: AUTOPLAY_TEST_KEYS.detail(variables.testId)
      });
    },
    onError: (error: any) => {
      toast.error(translateInterfaceText("Erro ao atualizar título"), {
        description: error?.response?.data?.message || error.message,
      });
    },
  });
};

/**
 * Hook para deletar teste
 */
export const useDeleteAutoplayTest = () => {
  useLanguage();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (testId: string) => autoplayTestService.deleteAutoplayTest(testId),
    onSuccess: (data, testId) => {
      toast.success(translateInterfaceText("Teste deletado com sucesso!"));
      // Invalidar cache dos testes
      queryClient.invalidateQueries({
        queryKey: AUTOPLAY_TEST_KEYS.lists()
      });
    },
    onError: (error: any) => {
      toast.error(translateInterfaceText("Erro ao deletar teste"), {
        description: error?.response?.data?.message || error.message,
      });
    },
  });
};

/**
 * Hook para atualizar distribuição de tráfego
 */
export const useUpdateAutoplayTestTraffic = () => {
  useLanguage();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      testId,
      payload,
    }: {
      testId: string;
      payload: UpdateAutoplayTestTrafficPayload;
    }) => autoplayTestService.updateTestTraffic(testId, payload),
    onSuccess: (data, variables) => {
      toast.success(translateInterfaceText("Distribuição de tráfego atualizada!"));
      // Invalidar cache do teste
      queryClient.invalidateQueries({
        queryKey: AUTOPLAY_TEST_KEYS.detail(variables.testId)
      });
    },
    onError: (error: any) => {
      toast.error(translateInterfaceText("Erro ao atualizar tráfego"), {
        description: error?.response?.data?.message || error.message,
      });
    },
  });
};

/**
 * Hook completo com todas as operações
 */
export const useAutoplayTest = (videoId?: string, userId?: string, testId?: string) => {
  useLanguage();
  const createTest = useCreateAutoplayTest();
  const updateTitle = useUpdateAutoplayTestTitle();
  const deleteTest = useDeleteAutoplayTest();
  const updateTraffic = useUpdateAutoplayTestTraffic();

  const userTests = useUserAutoplayTests(userId);
  const testVideos = useAutoplayTestVideos(testId);
  const testMetrics = useAutoplayTestMetrics(testId, {
    refetchInterval: testId ? 10000 : undefined
  });

  /**
   * Cria teste a partir de uma configuração local
   */
  const createTestFromConfig = async (config: AutoplayABTestConfig, title?: string) => {
    if (!videoId) {
      throw new Error(translateInterfaceText("videoId é obrigatório para criar um teste"));
    }

    if (!userId) {
      throw new Error(translateInterfaceText("userId é obrigatório para criar um teste"));
    }

    const payload = autoplayTestService.convertConfigToPayload(config, userId, title);
    return createTest.mutateAsync({ originalVideoId: videoId, payload });
  };

  /**
   * Ativa um teste (atualiza status para 'active')
   */
  const activateTest = async (testIdToActivate: string, config: AutoplayABTestConfig) => {
    if (!userId) {
      throw new Error(translateInterfaceText("userId é obrigatório para ativar um teste"));
    }

    const payload = autoplayTestService.convertConfigToPayload({
      ...config,
      status: 'active',
      startDate: new Date().toISOString(),
    }, userId);

    return createTest.mutateAsync({
      originalVideoId: videoId!,
      payload
    });
  };

  /**
   * Finaliza um teste (atualiza status para 'finished')
   */
  const finalizeTest = async (testIdToFinalize: string) => {
    return updateTitle.mutateAsync({
      testId: testIdToFinalize,
      payload: { autoplayTestTitle: 'Teste Finalizado' }, // TODO: criar endpoint específico para finalizar
    });
  };

  return {
    // Queries
    userTests,
    testVideos,
    testMetrics,

    // Mutations
    createTest,
    updateTitle,
    deleteTest,
    updateTraffic,

    // Helper functions
    createTestFromConfig,
    activateTest,
    finalizeTest,

    // Loading states
    isLoading: createTest.isPending || updateTitle.isPending || deleteTest.isPending || updateTraffic.isPending,
  };
};

export default useAutoplayTest;
